<script setup lang="ts">
import {
    ref, onMounted, type Ref, computed,
} from 'vue';
import { useQuasar } from 'quasar';
import type { FlightSchoolCourseStudent } from '@/types/FlightSchoolStudent';
import type FlightSchool from '@/types/FlightSchool';
import type FlightSchoolCourse from '@/types/FlightSchoolCourse';
import { printableStudentFromCourseStudent } from '@/types/FlightSchoolStudent';
import type { KnowledgeTestAgreementResponse } from '@/types/Response/FlightSchoolResponse';
import type {
    StudentRatingPerformance,
    StudentRatingTiming,
    StudyUnitStudyQuizPerformance,
} from '@/types/Performance';
import CoursePerformanceRepository from '@/repositories/CoursePerformanceRepository';
import PersonalInformationCard from '@/components/Student/PersonalInformationCard.vue';
import PersonalPerformance from '@/components/Student/PersonalPerformance.vue';
import StudentCommentForm from '@/components/Student/Comments/StudentCommentForm.vue';
import StudentCommentList from '@/components/Student/Comments/StudentCommentList.vue';
import StudentCommentRepository from '@/repositories/StudentCommentRepository';
import type { StudentCommentResponse } from '@/types/Response/StudentCommentResponse';
import { usePerformanceStore } from '@/stores/performance';
import FaaTestReportForm from './FaaTestReport/FaaTestReportForm.vue';
import FaaTestReportList from './FaaTestReport/FaaTestReportList.vue';
import FlightSchoolRepository from '@/repositories/FlightSchoolRepository';
import type { FaaTestReportResponse } from '@/types/Response/FaaTestReportResponse';

const props = defineProps<{
    school: FlightSchool,
    student: FlightSchoolCourseStudent,
    course: FlightSchoolCourse,
    kta: KnowledgeTestAgreementResponse|undefined,
    tab: string,
    studentTiming: StudentRatingTiming|null,
    studentPerformance: StudentRatingPerformance|null,
}>();

const $q = useQuasar();
const tab = ref<string>(props.tab);
const studentStudyQuizzes: Ref<StudyUnitStudyQuizPerformance[]> = ref([]);
const tableLoading = ref<boolean>(true);
const commentsLoading = ref<boolean>(true);
const commentMargins = computed(() => ($q.screen.gt.sm ? 'q-ml-xl' : 'q-ml-none'));
const comments: Ref<StudentCommentResponse[]> = ref([]);
const emit = defineEmits(['update:time']);

const getStudentComments = async (emitValue = false) => {
    commentsLoading.value = true;
    await StudentCommentRepository.getStudentComments(props.school, props.course, props.student)
        .then((result) => { comments.value = result; });

    if (emitValue) {
        emit('update:time');
    }
    commentsLoading.value = false;
};

const testReportClass = computed(() => ($q.screen.lt.md ? 'column' : 'row'));

const reports: Ref<FaaTestReportResponse[]> = ref([]);
const testReportsLoading = ref<boolean>(true);

const loadFaaTestReports = async () => {
    testReportsLoading.value = true;

    try {
        reports.value = await FlightSchoolRepository.getFaaTestReports(
            props.school,
            props.course,
            props.student,
        );
    } catch (error) {
        /**
         * @todo this should be replaced when front end error logging is implemented
         * https://gitlab.teamgleim.com/gleim-it/code-repos/ogs3/fsum-vue-app/-/issues/22
         */
        // eslint-disable-next-line no-console
        console.error(error);
    }

    testReportsLoading.value = false;
};

onMounted(async () => {
    studentStudyQuizzes.value = await CoursePerformanceRepository.getStudentStudyQuizzes(
        props.school,
        props.course,
        props.student,
    );

    const performanceStore = usePerformanceStore();
    performanceStore.setStudyQuestions(studentStudyQuizzes.value);

    tableLoading.value = false;
    getStudentComments();

    await loadFaaTestReports();
});
</script>
<template>
    <div class="fsum-tabs">
        <q-tabs
            v-model="tab"
            indicator-color="red"
            class="q-mb-xl q-ml-xl"
            breakpoint="0"
            align="left"
            no-caps
        >
            <q-tab
                name="Student Information"
                label="Student Information"
            />
            <q-tab
                name="Grade Book"
                label="Grade Book"
            />
            <q-tab
                name="FAA Test Reports"
                label="FAA Test Reports"
            />
        </q-tabs>
        <q-tab-panels
            v-model="tab"
            animated
            class="tab-pages"
        >
            <q-tab-panel name="Student Information">
                <div class="row q-gutter-y-xl justify-between">
                    <div class="col-12">
                        <PersonalInformationCard
                            :printable-student="printableStudentFromCourseStudent(student)"
                            :current-course="course"
                            :kta="kta"
                        />
                    </div>
                    <div class="col-md-6 col-12">
                        <StudentCommentForm
                            :student="student"
                            :course="course"
                            :school="school"
                            @update:comments="getStudentComments(true)"
                        />
                    </div>
                    <div
                        class="col-md col-12"
                        :class="commentMargins"
                    >
                        <StudentCommentList
                            :comments="comments"
                            :loading="commentsLoading"
                            :student="student"
                            @update:comments="getStudentComments(true)"
                        />
                    </div>
                </div>
            </q-tab-panel>
            <q-tab-panel name="Grade Book">
                <div class="row">
                    <PersonalPerformance
                        :student-performance="studentPerformance"
                        :student-study-quizzes="studentStudyQuizzes"
                        :student-timing="studentTiming"
                        :loading="tableLoading"
                    />
                </div>
            </q-tab-panel>
            <q-tab-panel name="FAA Test Reports">
                <div
                    class="q-gutter-y-xl q-gutter-x-xl justify-between"
                    :class="testReportClass"
                >
                    <div class="col-md-6 col-12">
                        <FaaTestReportForm
                            :student="student"
                            @update:reports="loadFaaTestReports"
                        />
                    </div>
                    <div class="col-md col-12">
                        <FaaTestReportList
                            :student="student"
                            :reports="reports"
                            :loading="testReportsLoading"
                            @update:reports="loadFaaTestReports"
                        />
                    </div>
                </div>
            </q-tab-panel>
        </q-tab-panels>
    </div>
</template>
<style scoped>
</style>
